import { ImpresoraProps } from '../../base/Impresora/interfaces/impresora.ts'
import { PuntoVentaProps } from '../../../interfaces/puntoVenta.ts'
import { SucursalProps } from '../../../interfaces/sucursal.ts'
import { TipoPedido } from './mesa.interface.ts'

/**
 * Tipos de comanda según el motivo del envío a cocina.
 * - NUEVA: primer envío del pedido, contiene todos los productos.
 * - MODIFICACION: envío posterior, contiene solo lo agregado, cambiado o quitado.
 * - CANCELACION: aviso a cocina de que el pedido fue cancelado (estado CANCELADO).
 */
export const TIPO_COMANDA = {
  NUEVA: 'NUEVA',
  MODIFICACION: 'MODIFICACION',
  CANCELACION: 'CANCELACION',
} as const

export type TipoComandaProps = (typeof TIPO_COMANDA)[keyof typeof TIPO_COMANDA]

/**
 * Tipo de cambio de una línea de comanda respecto al envío anterior.
 * - NUEVO: el producto no existía en envíos anteriores.
 * - ACTUALIZADO: cambió la cantidad, los modificadores, las variaciones de receta o las notas
 *   de un producto ya enviado.
 * - ELIMINADO: el producto se quitó del pedido (cocina no debe prepararlo).
 */
export const TIPO_CAMBIO_COMANDA = {
  NUEVO: 'NUEVO',
  ACTUALIZADO: 'ACTUALIZADO',
  ELIMINADO: 'ELIMINADO',
} as const

export type TipoCambioComanda = (typeof TIPO_CAMBIO_COMANDA)[keyof typeof TIPO_CAMBIO_COMANDA]

/**
 * Tipo de variación sobre la receta base de un producto.
 * - SIN: ingrediente quitado.
 * - EXTRA: ingrediente agregado (normalmente con cobro).
 * - RECETA: ajuste de cantidad de un ingrediente de la receta.
 */
export const TIPO_VARIACION_COMANDA = {
  SIN: 'SIN',
  EXTRA: 'EXTRA',
  RECETA: 'RECETA',
} as const

export type TipoVariacionComanda = (typeof TIPO_VARIACION_COMANDA)[keyof typeof TIPO_VARIACION_COMANDA]

/** Modificador de un producto, con solo los datos que se imprimen en la comanda. */
export interface ComandaModificadorProps {
  /** Nombre del modificador, ej. "Gaseosa" */
  nombre: string
  /** Opción elegida, ej. "Fría" */
  opcion: string | null
  /**
   * Unidad de medida, ej. "2L". Distingue variantes del mismo modificador y permite detectar
   * su cambio entre comandas. null para la unidad por defecto (no se imprime).
   */
  unidadMedida: string | null
  /** Cantidad */
  cantidad: number
}

/** Variación sobre la receta base de un producto, con solo los datos que se imprimen. */
export interface ComandaVariacionProps {
  /** Nombre del ingrediente */
  nombre: string
  /** Tipo de variación */
  tipo: TipoVariacionComanda
  /**
   * Unidad de medida de la cantidad, ej. "g", "porción". Necesaria en EXTRA y RECETA, donde la
   * cantidad sin unidad es ambigua (50 g de queso). null en SIN o cuando es la unidad por defecto.
   */
  unidadMedida: string | null
  /** Cantidad (1 para SIN) */
  cantidad: number
}

/**
 * Lo que cocina debe preparar de un producto en un momento dado.
 * Comparar dos estados permite detectar cambios aunque la cantidad sea la misma.
 */
export interface ComandaLineaEstadoProps {
  /** Cantidad del producto */
  cantidad: number
  /** Modificadores elegidos */
  modificadores: ComandaModificadorProps[]
  /** Notas unificadas (nota, notas rápidas y detalle extra) en el orden en que se imprimen */
  notas: string[]
  /**
   * Unidad de medida del producto, ej. "Porción", "Jarra". Distingue presentaciones del mismo
   * artículo y permite detectar su cambio entre comandas. null para la unidad por defecto (no se imprime).
   */
  unidadMedida: string | null
  /** Variaciones sobre la receta base */
  variaciones: ComandaVariacionProps[]
}

/**
 * Línea de una comanda: un producto con su estado antes y después de este envío.
 * Solo contiene los datos necesarios para cocina (sin precios ni montos).
 */
export interface ComandaLineaProps {
  /** Estado después de este envío; null cuando el producto fue ELIMINADO */
  actual: ComandaLineaEstadoProps | null
  /** Estado enviado anteriormente a cocina; null cuando el producto es NUEVO */
  anterior: ComandaLineaEstadoProps | null
  /**
   * Áreas a las que se enruta el producto: _id de rest_areas (ArticuloPrecio.areas de la sucursal)
   * o, si el artículo no tiene áreas, sus impresoras (ArticuloOperacionProps.impresoras[]._id).
   * Vacío cuando no tiene ninguna: se imprime en la impresora general (área GENERAL).
   */
  areasImpresion: string[]
  /** Código del artículo */
  codigoArticulo: string
  /** Nombre del artículo al momento de comandar */
  nombreArticulo: string
  /**
   * Número de ítem del producto dentro del pedido (PedidoProps.productos[].nroItem).
   * Identifica el producto entre comandas: el backend compara por nroItem.
   */
  nroItem: number
  /**
   * Número de la última comanda que envió este producto a cocina, para que cocina sepa qué ticket
   * se corrige. null cuando el producto es NUEVO.
   */
  numeroComandaAnterior: number | null
  /** Tipo de cambio respecto al envío anterior */
  tipoCambio: TipoCambioComanda
}

/**
 * Estado de una comanda en un área con pantalla de cocina.
 * - PENDIENTE: llegó a cocina, nadie la tomó.
 * - EN_PREPARACION: un cocinero la tomó.
 * - LISTO: lista para salir.
 * - ENTREGADO: el mesero la retiró.
 * - ANULADA: la anuló una cancelación o una corrección; solo se llega desde el flujo de pedidos.
 */
export const ESTADO_COCINA = {
  PENDIENTE: 'PENDIENTE',
  EN_PREPARACION: 'EN_PREPARACION',
  LISTO: 'LISTO',
  ENTREGADO: 'ENTREGADO',
  ANULADA: 'ANULADA',
} as const

export type EstadoCocinaProps = (typeof ESTADO_COCINA)[keyof typeof ESTADO_COCINA]

/** Estados en los que el área sigue abierta para cocina */
export const ESTADOS_COCINA_ABIERTOS: EstadoCocinaProps[] = [
  ESTADO_COCINA.PENDIENTE,
  ESTADO_COCINA.EN_PREPARACION,
  ESTADO_COCINA.LISTO,
]

/**
 * Cómo trabaja un área (impresora) con las comandas.
 * - IMPRESION: sin monitor; la comanda solo se imprime y reimprime (comercios pequeños).
 * - PANTALLA: la comanda aparece en la pantalla de cocina con estados.
 * - AMBOS: pantalla de cocina y comanda impresa.
 */
export const MODO_SALIDA = {
  IMPRESION: 'IMPRESION',
  PANTALLA: 'PANTALLA',
  AMBOS: 'AMBOS',
} as const

export type ModoSalidaProps = (typeof MODO_SALIDA)[keyof typeof MODO_SALIDA]

/** Área usada por las líneas sin área ni impresora asignada (se imprimen en la impresora general) */
export const REST_AREA_GENERAL = 'GENERAL'

/** Pseudo-área para las pantallas de cocina que ven todas las áreas de la sucursal */
export const REST_AREA_TODAS = 'TODAS'

/** Cambio de estado de un área, base de las métricas de tiempo */
export interface ComandaTransicionProps {
  estado: EstadoCocinaProps
  fecha: string
  usuario: string
}

/** Resultado de un intento de impresión o reimpresión de la comanda en un área */
export interface ComandaImpresionProps {
  contingencia: boolean
  /** Identificador del equipo que imprimió (opcional, lo envía el frontend) */
  equipo: string | null
  /** fecha string DateDMYHHMMSS */
  fecha: string
  ok: boolean
  usuario: string
}

/** Espacio (RestEspacio) donde está el cliente: salón principal, terraza, bar... */
export interface ComandaEspacioProps {
  _id: string | null
  descripcion: string | null
}

/** Estado operativo de la comanda en un área (impresora) */
export interface ComandaAreaProps {
  /**
   * _id del área (rest_areas). Si el artículo no tiene áreas asignadas, _id de su impresora
   * (área implícita, modo IMPRESION). REST_AREA_GENERAL si no tiene ninguna.
   */
  area: string
  /** null en modo IMPRESION */
  estado: EstadoCocinaProps | null
  /** true si al menos una impresión en el área salió bien */
  impresa: boolean
  impresiones: ComandaImpresionProps[]
  /** Impresoras donde imprimir esta área (configuración actual del área) */
  impresoras: ImpresoraProps[]
  /** Copiado de la configuración del área al crear la comanda */
  modo: ModoSalidaProps
  /** Nombre del área (o de la impresora usada como área) */
  nombre: string
  /** true si es un área registrada (Area); false si es una impresora usada como área o GENERAL */
  registrada: boolean
  transiciones: ComandaTransicionProps[]
}

/**
 * Comanda: nota enviada a cocina con lo que debe prepararse (contenido inmutable).
 * El backend la genera al registrar, actualizar o cancelar un pedido cuando cambian sus productos,
 * calculando la diferencia contra el envío anterior. Es inmutable una vez creada;
 * solo cambian sus datos de impresión y reimpresión.
 */
export interface ComandaProps {
  /** Identificador de la comanda (colección rest_comandas) */
  _id: string
  /** Una entrada por área que recibe líneas de esta comanda */
  areas: ComandaAreaProps[]
  /** Fecha de generación de la comanda */
  createdAt: string
  /**
   * Mutable, igual que mesa. Las mesas solo son únicas dentro de un espacio:
   * cocina necesita "Terraza · Mesa 5", no solo "Mesa 5".
   */
  espacio: ComandaEspacioProps
  /**
   * Productos de este envío. En NUEVA contiene todo el pedido; en MODIFICACION solo los productos
   * nuevos, cambiados o eliminados; en CANCELACION todos los enviados, como ELIMINADO.
   */
  lineas: ComandaLineaProps[]
  /** Mutable: se actualiza en la misma transacción si el pedido cambia de mesa */
  mesa: { nombre: string }
  /** Nota general del pedido al momento de comandar */
  nota: string | null
  /** Número correlativo de la comanda dentro del pedido (1, 2, 3...). Identifica la comanda */
  numeroComanda: number
  /** Inmutable */
  numeroOrden: number
  /** Inmutable */
  numeroPedido: number
  pedidoId: string
  puntoVenta: PuntoVentaProps
  // Referencia extendida: solo lo que cocina muestra
  /** Sucursal y punto de venta del pedido al momento de comandar (misma estructura que el pedido) */
  sucursal: SucursalProps
  /** Motivo del envío a cocina */
  tipo: TipoComandaProps
  tipoPedido: TipoPedido | null
  updatedAt: string
  /** Usuario que registró, actualizó o canceló el pedido */
  usucre: string
}
